import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const POLL_MS = 15 * 1000;
const ELO_KEY = "codeforcesRankedElo";
const HISTORY_KEY = "codeforcesRankedHistory";
const SESSION_KEY = "codeforcesRankedSession";

const MODES = [
  { id: "sprint", label: "Sprint", sublabel: "1 hour", durationMs: 60 * 60 * 1000, incrementMs: 0, weight: 0.92, icon: "fa-bolt" },
  { id: "sprint_inc", label: "Sprint+", sublabel: "1h · +5m/solve", durationMs: 60 * 60 * 1000, incrementMs: 5 * 60 * 1000, weight: 0.95, icon: "fa-bolt" },
  { id: "standard", label: "Standard", sublabel: "2h 30m", durationMs: 150 * 60 * 1000, incrementMs: 0, weight: 1.0, icon: "fa-shield-halved" },
  { id: "standard_inc", label: "Standard+", sublabel: "2h30 · +15m/solve", durationMs: 150 * 60 * 1000, incrementMs: 15 * 60 * 1000, weight: 1.05, icon: "fa-shield-halved" },
  { id: "marathon", label: "Marathon", sublabel: "5 hours", durationMs: 300 * 60 * 1000, incrementMs: 0, weight: 1.12, icon: "fa-mountain" },
  { id: "marathon_inc", label: "Marathon+", sublabel: "5h · +30m/solve", durationMs: 300 * 60 * 1000, incrementMs: 30 * 60 * 1000, weight: 1.18, icon: "fa-mountain" },
];

const TIERS = [
  { min: 2500, name: "Grandmaster", color: "from-red-600 to-rose-700", badge: "bg-red-700 text-white" },
  { min: 2100, name: "Master", color: "from-orange-500 to-amber-600", badge: "bg-orange-500 text-white" },
  { min: 1700, name: "Diamond", color: "from-cyan-400 to-blue-500", badge: "bg-cyan-500 text-white" },
  { min: 1300, name: "Platinum", color: "from-teal-400 to-emerald-500", badge: "bg-teal-500 text-white" },
  { min: 900, name: "Gold", color: "from-yellow-400 to-amber-500", badge: "bg-yellow-400 text-black" },
  { min: 500, name: "Silver", color: "from-slate-300 to-slate-400", badge: "bg-slate-300 text-slate-800" },
  { min: 200, name: "Bronze", color: "from-amber-700 to-orange-800", badge: "bg-amber-700 text-white" },
  { min: 0, name: "Iron", color: "from-stone-500 to-stone-700", badge: "bg-stone-500 text-white" },
];

const problemKey = (problem) => `${problem.contestId}-${problem.index}`;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const getTier = (elo) => TIERS.find((t) => elo >= t.min) || TIERS[TIERS.length - 1];

/** Smooth Elo → CF difficulty center. Elo 0 centers near 1100 → ~950–1250 band. */
const eloToCfCenter = (elo) => {
  const e = Math.max(0, elo);
  return clamp(1100 + 1950 * (1 - Math.exp(-e / 1550)) + e * 0.1, 1100, 3400);
};

/** Approx inverse of eloToCfCenter via binary search. */
const cfToEloApprox = (cf) => {
  let lo = 0;
  let hi = 5000;
  for (let i = 0; i < 28; i++) {
    const mid = (lo + hi) / 2;
    if (eloToCfCenter(mid) < cf) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

/** Always expose ~300–400 CF rating span for every Elo. */
const difficultyBand = (elo) => {
  const center = eloToCfCenter(elo);
  const halfSpan = 175; // ~350 width
  let low = Math.round(clamp(center - halfSpan, 800, 3500) / 100) * 100;
  let high = Math.round(clamp(center + halfSpan, 800, 3500) / 100) * 100;
  if (high - low < 300) {
    const mid = (low + high) / 2;
    low = Math.round(clamp(mid - 150, 800, 3500) / 100) * 100;
    high = low + 300;
    if (high > 3500) {
      high = 3500;
      low = 3200;
    }
  } else if (high - low > 400) {
    const mid = (low + high) / 2;
    low = Math.round((mid - 200) / 100) * 100;
    high = low + 400;
  }
  return { center, low, high, sigma: (high - low) / 2.5 };
};

/** Beginners gain easier / lose less; high Elo gains harder / loses more. */
const asymmetricScale = (elo, rawDelta) => {
  const e = Math.max(0, elo);
  // Stronger beginner protection than before
  const gainMult = clamp(2.35 - e / 1300, 0.45, 2.35);
  const lossMult = clamp(0.22 + e / 1600, 0.22, 1.7);
  return rawDelta >= 0 ? rawDelta * gainMult : rawDelta * lossMult;
};

/** Recent form: average of last few deltas, scaled to CF shift. */
const recentFormShift = (history = []) => {
  const recent = history.slice(0, 5);
  if (!recent.length) return 0;
  const avg = recent.reduce((s, h) => s + (h.delta || 0), 0) / recent.length;
  return clamp(avg * 1.6, -100, 100);
};

/** Live pace: solving faster → push harder, else ease. */
const paceShift = (elo, solves, startedAt, now) => {
  if (!startedAt || !solves?.length) return 0;
  const hours = Math.max(1 / 60, (now - startedAt) / 3600000);
  const rate = solves.length / hours;
  const expectedRate = clamp(3.6 * Math.exp(-elo / 1800), 0.7, 3.6);
  const ratio = rate / Math.max(expectedRate, 0.01);
  return clamp(Math.log2(ratio) * 100, -140, 180);
};

/** Skip fine in Elo-space: skipping easier-than-you hurts more; harder hurts less. */
const skipPenalty = (elo, problemRating) => {
  const center = eloToCfCenter(elo);
  const relative = (center - (problemRating || center)) / 400; // + if easier than center
  const base = 80;
  // Beginners take softer skip hits
  const soft = clamp(0.55 + elo / 1400, 0.55, 1);
  return clamp(base * (1 + Math.max(-0.55, relative * 1.15)) * soft, 25, 180);
};

const mulberry32 = (seed) => {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const hashSeed = (...parts) => {
  let h = 2166136261;
  const str = parts.join("|");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/**
 * Weighted sample across a ~350 CF band.
 * Harder solves later push center via pace; form nudges too.
 */
const pickProblem = (problemsWithStats, ctx = {}) => {
  const {
    elo = 0,
    excludeKeys = [],
    solves = [],
    startedAt = null,
    now = Date.now(),
    history = [],
    jitterSeed = Date.now(),
  } = ctx;

  const exclude = new Set(excludeKeys);
  const pool = (problemsWithStats || []).filter((p) => p.rating && !p.isSolved && !exclude.has(problemKey(p)));
  if (!pool.length) return null;

  const band = difficultyBand(elo);
  const center = clamp(band.center + recentFormShift(history) + paceShift(elo, solves, startedAt, now), 800, 3500);
  const rng = mulberry32(hashSeed(jitterSeed, elo, solves.length, excludeKeys.length));
  const jitter = (rng() - 0.5) * band.sigma * 0.55;
  const aim = clamp(center + jitter, band.low, band.high);
  const sigma = band.sigma * (0.9 + rng() * 0.35);

  const scored = pool.map((p) => {
    const inBand = p.rating >= band.low - 100 && p.rating <= band.high + 100;
    const dist = (p.rating - aim) / sigma;
    const gauss = Math.exp(-0.5 * dist * dist);
    const pop = p.solvedCount ? clamp(Math.log10(p.solvedCount + 10) / 5, 0.7, 1.25) : 0.9;
    // Keep most mass inside the wide band; tiny weight outside for fallback diversity
    const bandW = inBand ? 1 : 0.08;
    return { p, w: gauss * pop * bandW };
  });

  const total = scored.reduce((s, x) => s + x.w, 0);
  if (total <= 0) {
    return pool.slice().sort((a, b) => Math.abs(a.rating - aim) - Math.abs(b.rating - aim))[0] || null;
  }

  let r = rng() * total;
  for (const item of scored) {
    r -= item.w;
    if (r <= 0) return item.p;
  }
  return scored[scored.length - 1].p;
};

const dynamicK = (mode, gamesPlayed, eventCount, surprise) => {
  const experience = clamp(1.4 - gamesPlayed * 0.03, 0.55, 1.4);
  const sample = clamp(0.75 + Math.sqrt(Math.max(eventCount, 0)) * 0.16, 0.75, 1.4);
  const surpriseFactor = clamp(1 + Math.abs(surprise) / 900, 1, 1.3);
  return 34 * (mode?.weight || 1) * experience * sample * surpriseFactor;
};

const estimatePerformanceRating = (eloBefore, solves, skips, startedAt, elapsedMs) => {
  const centerElo = eloBefore;
  let weighted = 0;
  let wSum = 0;
  let prevTime = startedAt;

  const events = [
    ...solves.map((s) => ({ ...s, kind: "solve" })),
    ...skips.map((s) => ({ ...s, kind: "skip", solvedAt: s.skippedAt })),
  ].sort((a, b) => (a.solvedAt || 0) - (b.solvedAt || 0));

  if (!events.length) return null;

  events.forEach((ev, i) => {
    const t = ev.solvedAt || ev.skippedAt || prevTime || Date.now();
    const gapMs = Math.max(20_000, t - (prevTime || t - 20_000));
    prevTime = t;

    if (ev.kind === "solve") {
      const base = cfToEloApprox(ev.rating || 800);
      const minutes = gapMs / 60000;
      const softTargetMin = clamp(12 + (ev.rating - 800) / 80, 8, 55);
      const speed = clamp(softTargetMin / minutes, 0.55, 1.9);
      // Harder than your center pays more; easier pays less
      const relativeHard = ((ev.rating || 800) - eloToCfCenter(eloBefore)) / 400;
      const hardBonus = relativeHard * 160;
      const speedBoost = (speed - 1) * 130;
      const orderW = 1 + i * 0.05;
      const w = orderW * (0.9 + speed * 0.15);
      weighted += (base + hardBonus + speedBoost) * w;
      wSum += w;
    } else {
      // Skip pulls PR down by difficulty-aware penalty
      const pen = skipPenalty(eloBefore, ev.rating);
      const w = 0.85;
      weighted += (centerElo - pen) * w;
      wSum += w;
    }
  });

  let pr = wSum > 0 ? weighted / wSum : centerElo;

  if (solves.length) {
    const hours = Math.max(1 / 60, elapsedMs / 3600000);
    const rate = solves.length / hours;
    const expectedRate = clamp(3.6 * Math.exp(-eloBefore / 1800), 0.7, 3.6);
    pr += clamp(Math.log2(rate / expectedRate) * 85, -160, 200);
  }

  // Extra skip drag if many skips vs solves (softer for beginners)
  if (skips.length) {
    const soft = clamp(0.55 + eloBefore / 1400, 0.55, 1);
    pr -= skips.length * 18 * soft * (1 + skips.length / Math.max(solves.length + skips.length, 1));
  }

  return pr;
};

const computeMatchResult = ({ eloBefore, solves = [], skips = [], elapsedMs, startedAt, mode, history = [] }) => {
  const H = Math.max(0, elapsedMs) / 3600000;
  const games = history.length;
  const events = solves.length + skips.length;

  if (!events) {
    const bleedBase = 4 * H * (mode?.weight || 1);
    const scaledLoss = asymmetricScale(eloBefore, -bleedBase);
    const bleed = Math.round(clamp(-scaledLoss, 0, 28));
    const delta = -bleed;
    return {
      delta,
      eloAfter: Math.max(0, eloBefore + delta),
      performanceRating: null,
      effectiveK: null,
      surprise: 0,
      detail: { kind: "idle", bleed },
    };
  }

  const pr = estimatePerformanceRating(eloBefore, solves, skips, startedAt, elapsedMs);
  const surprise = pr - eloBefore;
  const K = dynamicK(mode, games, events, surprise);

  let raw = (K * surprise) / 400;
  raw = Math.tanh(raw / 55) * 55;

  // Beginner climb: any solid solve session gets a fading Elo gift
  if (solves.length > 0 && eloBefore < 1000) {
    const climb = ((1000 - eloBefore) / 1000) * (14 + 5 * Math.min(solves.length, 6));
    raw += climb;
  }

  raw = asymmetricScale(eloBefore, raw);

  const durationH = (mode?.durationMs || elapsedMs) / 3600000;
  const fill = clamp(H / Math.max(durationH, 0.01), 0, 1);
  if (solves.length >= 2 && fill > 0.3) {
    raw += asymmetricScale(eloBefore, (fill - 0.3) * 6 * (mode?.weight || 1));
  }

  const delta = Math.round(raw);
  const eloAfter = Math.max(0, eloBefore + delta);

  return {
    delta,
    eloAfter,
    performanceRating: Math.round(pr),
    effectiveK: Math.round(K * 10) / 10,
    surprise: Math.round(surprise),
    detail: { kind: "performance", pr, K, raw },
  };
};


const formatTime = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const getCfBadge = (rating) => {
  if (rating < 1200) return "bg-gray-500 text-white";
  if (rating < 1400) return "bg-green-500 text-white";
  if (rating < 1600) return "bg-cyan-500 text-white";
  if (rating < 1900) return "bg-blue-500 text-white";
  if (rating < 2100) return "bg-purple-500 text-white";
  if (rating < 2400) return "bg-yellow-500 text-black";
  if (rating < 3000) return "bg-red-500 text-white";
  return "bg-red-700 text-white";
};

const loadElo = () => {
  const raw = localStorage.getItem(ELO_KEY);
  const n = raw === null ? 0 : Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

function Ranked({ username, problemsWithStats, toggleDarkMode, isDarkMode }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("hub"); // hub | match | results
  const [elo, setElo] = useState(loadElo);
  const [matchHistory, setMatchHistory] = useState(loadHistory);
  const [selectedMode, setSelectedMode] = useState("standard");
  const [eloBefore, setEloBefore] = useState(0);
  const [modeId, setModeId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [currentProblem, setCurrentProblem] = useState(null);
  const [usedKeys, setUsedKeys] = useState([]);
  const [solves, setSolves] = useState([]);
  const [skips, setSkips] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [lastPollAt, setLastPollAt] = useState(null);
  const [pollStatus, setPollStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [poolExhausted, setPoolExhausted] = useState(false);
  const [restored, setRestored] = useState(false);

  const advancingRef = useRef(false);
  const matchRef = useRef({});
  const onAcceptRef = useRef(null);
  const endMatchRef = useRef(null);

  const mode = MODES.find((m) => m.id === (modeId || selectedMode)) || MODES[1];
  const tier = getTier(elo);
  const remainingMs = deadline ? deadline - now : 0;
  const elapsedMs = startedAt ? Math.min(now, deadline || now) - startedAt : 0;
  const timerUrgent = remainingMs > 0 && remainingMs < 5 * 60 * 1000;

  useEffect(() => {
    matchRef.current = { phase, currentProblem, usedKeys, solves, skips, eloBefore, modeId, startedAt, deadline, selectedMode };
  }, [phase, currentProblem, usedKeys, solves, skips, eloBefore, modeId, startedAt, deadline, selectedMode]);

  const projected = useMemo(() => {
    if (phase !== "match" || !startedAt) return null;
    const m = MODES.find((x) => x.id === modeId);
    if (!m) return null;
    return computeMatchResult({
      eloBefore,
      solves,
      skips,
      elapsedMs,
      startedAt,
      mode: m,
      history: matchHistory,
    });
  }, [phase, startedAt, modeId, eloBefore, solves, skips, elapsedMs, matchHistory]);

  const liveBand = useMemo(() => difficultyBand(elo), [elo]);

  const chartData = useMemo(() => {
    const chronological = [...matchHistory].reverse();
    const points = [{ match: 0, elo: chronological[0]?.eloBefore ?? elo }];
    chronological.forEach((h, i) => {
      points.push({ match: i + 1, elo: h.eloAfter, delta: h.delta, mode: h.mode });
    });
    if (points.length === 1 && elo === 0) {
      return [{ match: 0, elo: 0 }];
    }
    return points;
  }, [matchHistory, elo]);

  const persistSession = useCallback((payload) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ username, ...payload, savedAt: Date.now() }));
  }, [username]);

  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
  }, []);

  const saveElo = (value) => {
    const v = Math.max(0, value);
    setElo(v);
    localStorage.setItem(ELO_KEY, String(v));
  };

  const saveHistory = (entries) => {
    setMatchHistory(entries);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
  };

  // Restore active session
  useEffect(() => {
    if (restored) return;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) {
        setRestored(true);
        return;
      }
      const saved = JSON.parse(raw);
      if (saved.username !== username || saved.phase !== "match") {
        clearSession();
        setRestored(true);
        return;
      }
      setEloBefore(saved.eloBefore ?? 0);
      setModeId(saved.modeId);
      setSelectedMode(saved.modeId || "standard");
      setStartedAt(saved.startedAt);
      setDeadline(saved.deadline);
      setCurrentProblem(saved.currentProblem);
      setUsedKeys(saved.usedKeys || []);
      setSolves(saved.solves || []);
      setSkips(saved.skips || []);
      setPoolExhausted(!!saved.poolExhausted);
      setPhase("match");
    } catch {
      clearSession();
    } finally {
      setRestored(true);
    }
  }, [username, restored, clearSession]);

  // Clock tick
  useEffect(() => {
    if (phase !== "match") return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const endMatch = useCallback(
    (reason = "time") => {
      const snap = matchRef.current;
      if (snap.phase !== "match") return;

      const m = MODES.find((x) => x.id === snap.modeId);
      if (!m) return;

      const endedAt = Date.now();
      const elapsed = Math.max(0, Math.min(endedAt, snap.deadline || endedAt) - (snap.startedAt || endedAt));
      const result = computeMatchResult({
        eloBefore: snap.eloBefore,
        solves: snap.solves || [],
        skips: snap.skips || [],
        elapsedMs: elapsed,
        startedAt: snap.startedAt,
        mode: m,
        history: loadHistory(),
      });

      const entry = {
        id: `${endedAt}-${Math.random().toString(36).slice(2, 8)}`,
        mode: snap.modeId,
        startedAt: snap.startedAt,
        endedAt,
        elapsedMs: elapsed,
        eloBefore: snap.eloBefore,
        eloAfter: result.eloAfter,
        delta: result.delta,
        performanceRating: result.performanceRating,
        effectiveK: result.effectiveK,
        surprise: result.surprise,
        reason,
        solves: snap.solves || [],
        skips: snap.skips || [],
      };

      const nextHistory = [entry, ...loadHistory()].slice(0, 100);
      saveHistory(nextHistory);
      saveElo(result.eloAfter);
      clearSession();

      setLastResult(entry);
      setPhase("results");
      setCurrentProblem(null);
      setDeadline(null);
      setPollStatus("idle");
      advancingRef.current = false;
    },
    [clearSession]
  );

  useEffect(() => {
    endMatchRef.current = endMatch;
  }, [endMatch]);

  // Auto-end when timer hits 0
  useEffect(() => {
    if (phase !== "match" || !deadline) return;
    if (now >= deadline) endMatch("time");
  }, [phase, deadline, now, endMatch]);

  const advanceToNext = useCallback(
    (nextSolves, nextSkips, nextUsed, deadlineOverride) => {
      const snap = matchRef.current;
      const nextProblem = pickProblem(problemsWithStats, {
        elo: snap.eloBefore,
        excludeKeys: nextUsed,
        solves: nextSolves,
        startedAt: snap.startedAt,
        now: Date.now(),
        history: loadHistory(),
        jitterSeed: Date.now() + nextSolves.length * 997 + nextSkips.length * 131,
      });

      const nextDeadline = deadlineOverride ?? snap.deadline;

      if (!nextProblem) {
        setSolves(nextSolves);
        setSkips(nextSkips);
        setUsedKeys(nextUsed);
        setCurrentProblem(null);
        setDeadline(nextDeadline);
        setPoolExhausted(true);
        persistSession({
          phase: "match",
          eloBefore: snap.eloBefore,
          modeId: snap.modeId,
          startedAt: snap.startedAt,
          deadline: nextDeadline,
          currentProblem: null,
          usedKeys: nextUsed,
          solves: nextSolves,
          skips: nextSkips,
          poolExhausted: true,
        });
        advancingRef.current = false;
        setTimeout(() => endMatchRef.current?.("pool"), 800);
        return;
      }

      const nextKey = problemKey(nextProblem);
      const usedWithNext = [...nextUsed, nextKey];

      setSolves(nextSolves);
      setSkips(nextSkips);
      setUsedKeys(usedWithNext);
      setCurrentProblem(nextProblem);
      setDeadline(nextDeadline);
      setPoolExhausted(false);
      persistSession({
        phase: "match",
        eloBefore: snap.eloBefore,
        modeId: snap.modeId,
        startedAt: snap.startedAt,
        deadline: nextDeadline,
        currentProblem: nextProblem,
        usedKeys: usedWithNext,
        solves: nextSolves,
        skips: nextSkips,
        poolExhausted: false,
      });
      advancingRef.current = false;
    },
    [problemsWithStats, persistSession]
  );

  const handleAccept = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    const snap = matchRef.current;
    const problem = snap.currentProblem;
    if (!problem) {
      advancingRef.current = false;
      return;
    }

    const key = problemKey(problem);
    const nextSolves = [
      ...(snap.solves || []),
      {
        key,
        contestId: problem.contestId,
        index: problem.index,
        name: problem.name,
        rating: problem.rating,
        solvedAt: Date.now(),
      },
    ];
    const nextSkips = snap.skips || [];
    const nextUsed = (snap.usedKeys || []).includes(key) ? snap.usedKeys : [...(snap.usedKeys || []), key];

    const m = MODES.find((x) => x.id === snap.modeId);
    const nextDeadline = m?.incrementMs ? (snap.deadline || Date.now()) + m.incrementMs : snap.deadline;

    advanceToNext(nextSolves, nextSkips, nextUsed, nextDeadline);
  }, [advanceToNext]);

  const handleSkip = useCallback(() => {
    if (advancingRef.current) return;
    const snap = matchRef.current;
    const problem = snap.currentProblem;
    if (!problem || snap.phase !== "match") return;

    advancingRef.current = true;
    const key = problemKey(problem);
    const nextSkips = [
      ...(snap.skips || []),
      {
        key,
        contestId: problem.contestId,
        index: problem.index,
        name: problem.name,
        rating: problem.rating,
        skippedAt: Date.now(),
        penalty: Math.round(skipPenalty(snap.eloBefore, problem.rating)),
      },
    ];
    const nextSolves = snap.solves || [];
    const nextUsed = (snap.usedKeys || []).includes(key) ? snap.usedKeys : [...(snap.usedKeys || []), key];

    advanceToNext(nextSolves, nextSkips, nextUsed, snap.deadline);
  }, [advanceToNext]);

  useEffect(() => {
    onAcceptRef.current = handleAccept;
  }, [handleAccept]);

  // Poll submissions
  useEffect(() => {
    if (phase !== "match" || !username || !currentProblem) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        setPollStatus("checking");
        const response = await fetch(`https://codeforces.com/api/user.status?handle=${username}&count=50`);
        const data = await response.json();
        if (cancelled) return;

        setLastPollAt(Date.now());
        if (data.status !== "OK") {
          setPollStatus("error");
          return;
        }

        const { contestId, index } = matchRef.current.currentProblem || {};
        const accepted = (data.result || []).some(
          (sub) => sub.verdict === "OK" && sub.problem?.contestId === contestId && sub.problem?.index === index
        );

        setPollStatus(accepted ? "found" : "watching");
        if (accepted) onAcceptRef.current?.();
      } catch {
        if (!cancelled) setPollStatus("error");
      }
    };

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [phase, username, currentProblem]);

  const handleStart = () => {
    setError(null);
    const m = MODES.find((x) => x.id === selectedMode);
    if (!m) return;

    const startElo = loadElo();
    const first = pickProblem(problemsWithStats, {
      elo: startElo,
      excludeKeys: [],
      solves: [],
      startedAt: Date.now(),
      now: Date.now(),
      history: loadHistory(),
      jitterSeed: Date.now(),
    });
    if (!first) {
      setError("No unsolved rated problems available for your Elo band. Try again after solving fewer, or check problem cache.");
      return;
    }

    const start = Date.now();
    const end = start + m.durationMs;
    const key = problemKey(first);

    setEloBefore(startElo);
    setModeId(m.id);
    setStartedAt(start);
    setDeadline(end);
    setCurrentProblem(first);
    setUsedKeys([key]);
    setSolves([]);
    setSkips([]);
    setPoolExhausted(false);
    setLastResult(null);
    setPhase("match");
    setNow(start);

    persistSession({
      phase: "match",
      eloBefore: startElo,
      modeId: m.id,
      startedAt: start,
      deadline: end,
      currentProblem: first,
      usedKeys: [key],
      solves: [],
      skips: [],
      poolExhausted: false,
    });
  };

  const handleBackToHub = () => {
    setPhase("hub");
    setLastResult(null);
    setCurrentProblem(null);
    setError(null);
  };

  const problemUrl = currentProblem
    ? `https://codeforces.com/problemset/problem/${currentProblem.contestId}/${currentProblem.index}`
    : null;

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-violet-50/30 to-fuchsia-50/40"
      }`}
    >
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:justify-between lg:items-center py-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => (phase === "match" ? null : navigate("/"))}
                disabled={phase === "match"}
                className={`p-3 rounded-2xl transition-all duration-300 ${
                  phase === "match"
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:scale-110"
                } ${
                  isDarkMode
                    ? "bg-gradient-to-br from-slate-700 to-slate-800 text-slate-300"
                    : "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600"
                }`}
                title={phase === "match" ? "Finish or end match first" : "Back to Home"}
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div className={`p-3 rounded-2xl bg-gradient-to-br ${tier.color}`}>
                <i className="fas fa-ranking-star text-white text-xl"></i>
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Ranked</h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  Solo ladder for {username} — separate from CF rating
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {phase === "match" && (
                <button
                  onClick={() => endMatch("forfeit")}
                  className={`px-4 py-2 rounded-xl font-medium transition-all hover:scale-105 ${
                    isDarkMode ? "bg-red-500/15 text-red-300 border border-red-500/30" : "bg-red-50 text-red-600 border border-red-200"
                  }`}
                >
                  <i className="fas fa-flag mr-2"></i>
                  End Match
                </button>
              )}
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
                }`}
                title="Toggle dark mode"
              >
                <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        {error && (
          <div className={`mb-6 p-4 rounded-2xl ${isDarkMode ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-red-50 text-red-700 border border-red-200"}`}>
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </div>
        )}

        {phase === "hub" && (
          <div className="space-y-8">
            <div className="grid lg:grid-cols-5 gap-6">
              <div
                className={`lg:col-span-2 rounded-3xl p-8 bg-gradient-to-br ${tier.color} text-white shadow-2xl relative overflow-hidden`}
              >
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative">
                  <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">{tier.name}</div>
                  <div className="text-6xl font-black mb-1">{Math.round(elo)}</div>
                  <div className="text-white/80 text-sm mb-6">Ranked Elo · floor 0 · independent of CF</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white/15 rounded-xl p-3 backdrop-blur-sm">
                      <div className="opacity-80">Matches</div>
                      <div className="text-xl font-bold">{matchHistory.length}</div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 backdrop-blur-sm">
                      <div className="opacity-80">CF band</div>
                      <div className="text-xl font-bold">
                        {liveBand.low}–{liveBand.high}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`lg:col-span-3 rounded-3xl p-8 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow-xl"
                }`}
              >
                <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Select mode</h2>
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
                  {MODES.map((m) => {
                    const active = selectedMode === m.id;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setSelectedMode(m.id)}
                        className={`text-left rounded-2xl p-4 border transition-all hover:scale-[1.02] ${
                          active
                            ? "bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white border-transparent shadow-lg"
                            : isDarkMode
                            ? "bg-slate-700/50 border-slate-600/50 text-slate-200"
                            : "bg-slate-50 border-slate-200 text-slate-800"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold">{m.label}</span>
                          <i className={`fas ${m.incrementMs ? "fa-plus-circle" : m.icon}`}></i>
                        </div>
                        <div className={`text-sm ${active ? "text-white/80" : isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{m.sublabel}</div>
                        <div className={`text-xs mt-2 ${active ? "text-white/70" : isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                          {m.incrementMs ? `+${m.incrementMs / 60000}m on each AC` : "Fixed timer"}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={handleStart}
                  disabled={!problemsWithStats?.length}
                  className="w-full py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow-xl hover:scale-[1.01] transition-all disabled:opacity-60"
                >
                  <i className="fas fa-play mr-2"></i>
                  Start {MODES.find((m) => m.id === selectedMode)?.label} Match
                </button>
                <p className={`text-xs mt-3 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                  CF band ~300–400. Skips allowed (easier skips hurt more). Beginners climb faster; high Elo loses easier.
                </p>
              </div>
            </div>

            <div
              className={`rounded-3xl p-6 ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow-xl"}`}
            >
              <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Elo history</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#334155" : "#e2e8f0"} />
                    <XAxis dataKey="match" stroke={isDarkMode ? "#94a3b8" : "#64748b"} label={{ value: "Match", position: "insideBottom", offset: -2 }} />
                    <YAxis stroke={isDarkMode ? "#94a3b8" : "#64748b"} domain={[0, "auto"]} />
                    <Tooltip
                      contentStyle={{
                        background: isDarkMode ? "#1e293b" : "#fff",
                        border: isDarkMode ? "1px solid #334155" : "1px solid #e2e8f0",
                        borderRadius: 12,
                      }}
                    />
                    <Line type="monotone" dataKey="elo" stroke="#a855f7" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div
              className={`rounded-3xl p-6 ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow-xl"}`}
            >
              <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Match history</h2>
              {matchHistory.length === 0 ? (
                <p className={isDarkMode ? "text-slate-400" : "text-slate-500"}>No matches yet. Play your first ranked game.</p>
              ) : (
                <div className="space-y-3">
                  {matchHistory.slice(0, 15).map((h) => {
                    const m = MODES.find((x) => x.id === h.mode);
                    return (
                      <div
                        key={h.id}
                        className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 ${
                          isDarkMode ? "bg-slate-900/50 border border-slate-700/40" : "bg-slate-50 border border-slate-200"
                        }`}
                      >
                        <div>
                          <div className={`font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                            {m?.label || h.mode} · {h.solves?.length || 0} solves
                            {h.skips?.length ? ` · ${h.skips.length} skips` : ""}
                          </div>
                          <div className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            {new Date(h.endedAt).toLocaleString()} · {formatTime(h.elapsedMs)}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {h.eloBefore} → {h.eloAfter}
                          </span>
                          <span
                            className={`px-3 py-1 rounded-lg text-sm font-bold ${
                              h.delta >= 0
                                ? isDarkMode
                                  ? "bg-emerald-500/20 text-emerald-300"
                                  : "bg-emerald-50 text-emerald-700"
                                : isDarkMode
                                ? "bg-red-500/20 text-red-300"
                                : "bg-red-50 text-red-700"
                            }`}
                          >
                            {h.delta >= 0 ? `+${h.delta}` : h.delta}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {phase === "match" && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { label: "Solves", value: solves.length, icon: "fa-check" },
                { label: "Skips", value: skips.length, icon: "fa-forward" },
                {
                  label: "Projected",
                  value: projected ? (projected.delta >= 0 ? `+${projected.delta}` : projected.delta) : "—",
                  icon: "fa-chart-line",
                },
                { label: "Mode", value: mode.label, icon: mode.icon },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`rounded-2xl p-5 ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow"}`}
                >
                  <div className={`text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    <i className={`fas ${stat.icon} mr-2`}></i>
                    {stat.label}
                  </div>
                  <div className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{stat.value}</div>
                </div>
              ))}
            </div>

            <div
              className={`rounded-3xl overflow-hidden ${
                isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/90 border border-slate-200/60 shadow-xl"
              }`}
            >
              <div
                className={`px-8 py-6 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${
                  isDarkMode ? "border-slate-700/50 bg-gradient-to-r from-violet-950/40 to-slate-900/80" : "border-slate-200/50 bg-gradient-to-r from-violet-50 to-fuchsia-50"
                }`}
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${isDarkMode ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
                      Live match
                    </span>
                    {currentProblem?.rating && (
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getCfBadge(currentProblem.rating)}`}>{currentProblem.rating}</span>
                    )}
                  </div>
                  {currentProblem ? (
                    <>
                      <h2 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        {currentProblem.contestId}
                        {currentProblem.index}. {currentProblem.name}
                      </h2>
                      <p className={`text-sm mt-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                        AC to continue{mode.incrementMs ? ` (+${mode.incrementMs / 60000}m)` : ""}. Skip hurts perf more on easier problems.
                      </p>
                    </>
                  ) : (
                    <h2 className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {poolExhausted ? "Problem pool exhausted — wrapping up..." : "Loading..."}
                    </h2>
                  )}
                </div>

                <div className="text-center lg:text-right">
                  <div className={`font-mono text-5xl font-bold tracking-tight ${timerUrgent ? "text-red-500 animate-pulse" : isDarkMode ? "text-violet-300" : "text-violet-700"}`}>
                    {formatTime(remainingMs)}
                  </div>
                  <div className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {pollStatus === "checking" ? "Checking submissions..." : "Auto-check every 15s"}
                  </div>
                  {lastPollAt && (
                    <div className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                      Last check {Math.max(0, Math.floor((Date.now() - lastPollAt) / 1000))}s ago
                    </div>
                  )}
                </div>
              </div>

              {currentProblem && (
                <div className="p-8">
                  <div className="flex flex-wrap gap-2 mb-6">
                    {(currentProblem.tags || []).map((tag) => (
                      <span key={tag} className={`px-3 py-1 rounded-lg text-sm ${isDarkMode ? "bg-slate-700/60 text-slate-300" : "bg-slate-100 text-slate-700"}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={problemUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-5 py-3 rounded-xl font-semibold bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow-lg hover:scale-105 transition-all"
                    >
                      <i className="fas fa-external-link-alt mr-2"></i>
                      Open Problem
                    </a>
                    <button
                      onClick={handleSkip}
                      className={`px-5 py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
                        isDarkMode ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      <i className="fas fa-forward mr-2"></i>
                      Skip (−~{Math.round(skipPenalty(eloBefore, currentProblem.rating))} perf)
                    </button>
                    <button
                      onClick={() => navigate(`/problem/${currentProblem.contestId}/${currentProblem.index}`)}
                      className={`px-5 py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
                        isDarkMode ? "bg-slate-700/70 text-slate-200" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      <i className="fas fa-info-circle mr-2"></i>
                      Local Details
                    </button>
                  </div>
                </div>
              )}
            </div>

            {solves.length > 0 && (
              <div className={`rounded-3xl p-6 ${isDarkMode ? "bg-slate-800/50 border border-slate-700/40" : "bg-white/80 border border-slate-200/60"}`}>
                <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Solves this match</h3>
                <div className="space-y-2">
                  {[...solves].reverse().map((s) => (
                    <div key={`${s.key}-${s.solvedAt}`} className="flex items-center justify-between gap-3">
                      <span className={`font-medium truncate ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                        {s.contestId}
                        {s.index}. {s.name}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold shrink-0 ${getCfBadge(s.rating)}`}>{s.rating}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {phase === "results" && lastResult && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div
              className={`rounded-3xl p-10 text-center ${
                isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow-xl"
              }`}
            >
              <div className={`inline-flex p-4 rounded-2xl mb-6 bg-gradient-to-br ${getTier(lastResult.eloAfter).color} text-white`}>
                <i className="fas fa-flag-checkered text-3xl"></i>
              </div>
              <h2 className={`text-3xl font-bold mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Match complete</h2>
              <p className={`mb-6 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                {MODES.find((m) => m.id === lastResult.mode)?.label} · {lastResult.solves.length} solves
                {lastResult.skips?.length ? ` · ${lastResult.skips.length} skips` : ""} · {formatTime(lastResult.elapsedMs)}
                {lastResult.reason === "forfeit" ? " · ended early" : ""}
                {lastResult.reason === "pool" ? " · pool exhausted" : ""}
              </p>

              <div
                className={`text-6xl font-black mb-2 ${
                  lastResult.delta >= 0 ? (isDarkMode ? "text-emerald-400" : "text-emerald-600") : isDarkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                {lastResult.delta >= 0 ? `+${lastResult.delta}` : lastResult.delta}
              </div>
              <div className={`text-lg mb-8 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                {lastResult.eloBefore} → <span className="font-bold">{lastResult.eloAfter}</span> Elo
                <span className={`ml-2 text-sm px-2 py-0.5 rounded ${getTier(lastResult.eloAfter).badge}`}>{getTier(lastResult.eloAfter).name}</span>
              </div>

              <div className={`grid grid-cols-2 gap-3 mb-8 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                <div className={`rounded-xl p-3 ${isDarkMode ? "bg-slate-900/50" : "bg-slate-50"}`}>
                  Perf rating: {lastResult.performanceRating ?? "—"}
                </div>
                <div className={`rounded-xl p-3 ${isDarkMode ? "bg-slate-900/50" : "bg-slate-50"}`}>
                  Effective K: {lastResult.effectiveK ?? "—"}
                </div>
              </div>

              {lastResult.solves.length > 0 && (
                <div className="text-left mb-8">
                  <h3 className={`font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Problems solved</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {lastResult.solves.map((s) => (
                      <div key={`${s.key}-${s.solvedAt}`} className="flex justify-between gap-2 text-sm">
                        <span className={`truncate ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {s.contestId}
                          {s.index}. {s.name}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${getCfBadge(s.rating)}`}>{s.rating}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleBackToHub}
                className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white hover:scale-105 transition-all"
              >
                Back to Ranked hub
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Ranked;
